import React, { useState, useEffect } from "react";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatCurrency, formatInteger } from "../helpers/formatNumber";
import { CashbackRequestsAdminService } from "../api/generated/services/CashbackRequestsAdminService";
import { CashbackRequestViewModel } from "../api/generated/models/CashbackRequestViewModel";
import { CashbackRequestState } from "../api/generated/models/CashbackRequestState";
import "../api/openApiConfig";
import CashbackRequestDetailsModal from "../components/AdminCashbackRequests/CashbackRequestDetailsModal";

const PAGE_SIZE = 100;

type StateFilter = "All" | CashbackRequestState;

const stateBadgeClass = (state?: string | null) =>
  state === "Accepted"
    ? "bg-green-100 text-green-800"
    : state === "Declined"
    ? "bg-red-100 text-red-800"
    : "bg-yellow-100 text-yellow-800";

const AdminCashbackRequests: React.FC = () => {
  const [requests, setRequests] = useState<CashbackRequestViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [stateFilter, setStateFilter] = useState<StateFilter>("All");
  const [selectedRequest, setSelectedRequest] = useState<CashbackRequestViewModel | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await CashbackRequestsAdminService.postApiCashbackRequestsAdminList({
          states: stateFilter === "All" ? undefined : [stateFilter],
          pagination: { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
        });
        if (ignore) return;
        setRequests(response.data ?? []);
        setTotal(response.total ?? 0);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch cashback requests");
        console.error("Error fetching cashback requests:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, stateFilter, refreshKey]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Cashback Requests</h2>
        <div className="flex items-end gap-4">
          <div className="w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md h-[42px]"
              value={stateFilter}
              onChange={(e) => {
                setPageNumber(1);
                setStateFilter(e.target.value as StateFilter);
              }}
            >
              <option value="All">All</option>
              {Object.values(CashbackRequestState).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="bg-white rounded-lg shadow p-4 min-w-[160px] w-fit">
            <div className="text-gray-500 text-sm">Total Requests</div>
            <div className="text-2xl font-semibold mt-1">{formatInteger(total)}</div>
          </div>
        </div>
      </div>

      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th className="whitespace-nowrap">Purchase Time</th>
              <th>Merchant</th>
              <th className="max-w-[120px]">Total Amount</th>
              <th className="max-w-[120px]">Total Cashback</th>
              <th className="max-w-[120px]">Customer Cashback</th>
              <th>State</th>
              <th className="whitespace-nowrap">Affiliate Type</th>
              <th className="whitespace-nowrap">Claim Sent</th>
              <th className="whitespace-nowrap">Order ID</th>
              <th>
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td className="whitespace-nowrap">
                  {request.purchaseTime
                    ? new Date(request.purchaseTime).toLocaleString("da-DK", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "-"}
                </td>
                <td>{request.merchantName || "-"}</td>
                <td className="max-w-[120px]">{formatCurrency(request.totalAmount ?? 0)}</td>
                <td className="max-w-[120px]">
                  {formatCurrency(request.totalAssignedCashback ?? 0)}
                </td>
                <td className="max-w-[120px]">
                  {formatCurrency(request.customerAssignedCashback ?? 0)}
                </td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stateBadgeClass(
                      request.state
                    )}`}
                  >
                    {request.state}
                  </span>
                </td>
                <td className="whitespace-nowrap">{request.affiliateTransactionType || "-"}</td>
                <td className="whitespace-nowrap">
                  {request.claimCreatedAutomatically ? "YES" : "-"}
                </td>
                <td className="whitespace-nowrap">{request.orderId || "-"}</td>
                <td className="whitespace-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRequest(request);
                      setDetailsModalOpen(true);
                    }}
                  >
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={() => setPageNumber((p) => p + 1)}
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>
      <CashbackRequestDetailsModal
        key={selectedRequest?.id ?? "none"}
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        request={selectedRequest}
        onRequestChanged={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default AdminCashbackRequests;
